import React, {useState, useMemo} from 'react';
import styled from 'styled-components';
import Typography from '@material-ui/core/Typography';
import Spinner from '../../../components/UI/Spinner';
import {useTranslation} from 'react-i18next';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import {useDispatch, useSelector} from 'react-redux';
import {putUserPreferences} from '../../../store/actions/user';
import {putUserPreferencesSelector} from '../../../store/selectors';
import {WithUserProps, withUser} from '../../../hooks/useAuth';
import {getUserGroups} from '../../authHOC';
import {IUserPreferenceTypeUI} from '@deep-planet/api-interfaces';

// Styled form container
const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

// Styled section title
const Title = styled(Typography)`
  && {
    margin-bottom: 2rem;
  }
`;

// Styled save button
const Btn = styled(Button)`
  && {
    margin-top: 1rem;
  }
`;
// Props passed to this component
interface Props extends WithUserProps {
  notificationTypes: IUserPreferenceTypeUI[]; // List of notification types from backend
}

// Main functional component
const Notifications = ({notificationTypes, user}: Props) => {
  // Check if the current user has admin privileges
  const isAdmin = useMemo(() => getUserGroups(user).includes('SUPERADMIN') || getUserGroups(user).includes('ADMIN'), [user]);
  const {t} = useTranslation(); // i18n translation hook
  const dispatch = useDispatch(); // Redux dispatch function
  const {loading} = useSelector(putUserPreferencesSelector); // Selector to track loading state

  // Create dynamic state: map notification types to their current values
  const [prefs, setPrefs] = useState<Record<string, boolean>>(
    notificationTypes?.reduce((acc, curr) => {
      acc[curr.type] = curr.value; // Initialize state with given values
      return acc;
    }, {} as Record<string, boolean>)
  );

  // Toggle individual preference checkbox
  const handleCheckboxChange = (type: string) => {
    setPrefs(prev => ({...prev, [type]: !prev[type]}));
  };

  // Submit updated preferences to backend
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Convert state into backend-friendly format
    const preferences = Object.entries(prefs).map(([type, value]) => ({
      type,
      value: Number(value), // Convert boolean to 0/1
    }));

    dispatch(putUserPreferences({preferences}));
  };

  // Track if any changes have been made compared to original values
  const changed = useMemo(() => {
    return notificationTypes?.some(nt => Boolean(nt.value) !== prefs[nt.type]);
  }, [prefs, notificationTypes]);

  return (
    <>
      {/* Section title */}
      <Title variant="body1">{t('account.preferences.notification.subtitle')}</Title>

      {/* Form wrapper */}
      {notificationTypes && (
        <Form onSubmit={handleSubmit}>
          {/* Loop through each notification type and render a checkbox */}
          {notificationTypes.map(({type, description, adminOnly, value}) => {
            // Skip rendering if user isn't admin and it's an admin-only setting
            if (adminOnly && !isAdmin) return null;

            return (
              <FormControlLabel
                key={type}
                style={{padding: '0 1rem'}}
                control={
                  <Checkbox
                    color="primary"
                    checked={prefs[type]} // Bind checkbox to dynamic state
                    onChange={() => handleCheckboxChange(type)} // Toggle on change
                    name={`${type}_checkbox`} // Unique checkbox name
                  />
                }
                label={t(description)} // Translate the label text
              />
            );
          })}

          {/* Submit/save button */}
          <Btn variant="contained" color="primary" type="submit" disabled={loading || !changed}>
            {t('account.personal.information.save')}
            {loading && <Spinner size={15} color="primary" />}
          </Btn>
        </Form>
      )}
    </>
  );
};

// Wrap component with user context
export default withUser(Notifications);
